/**
 * Who may open a record's notes, documents and attributes: legacy's own per-tool paths.
 *
 * Legacy gated these from inside the tool that owns the record: a tool's `Notes` command opens the
 * note form (view and add), and `Attributes {Entity}` opens the attribute editor, whose fields each
 * check their own `Attributes\{System}\{SourceTable}\{Type}` path (`EntityAttributeTypeLookup.
 * SecurityPath`, `AttributeEditControl.cs:166`). Documents were never gated beyond reaching the record:
 * every `Right Click Show Documents` command is `IsExcludedFromSecurity` (its base,
 * `ShowDocumentsRightClickCommand.cs:132`), which `SecurityService.HasAccess` answers with Allow, so
 * the Documents tab follows the tool (`open`) and those paths are not declared here. The notes, documents and attributes routes are shared by every
 * tool and receive only `sourceTable` + `sourceId`, so this map names, per `dbo.SourceTable` name,
 * the paths legacy would have checked. Any one path of a list is enough: `Ledger Transaction Group`
 * records belong to Cash Count, Vault Transaction and Cashier Shift Management alike, and the server
 * cannot tell which grid asked. A record type with no entry is refused; map it before a tool shows its tabs.
 *
 * The server enforces these rules and the SPA evaluates the same ones to decide which tabs to show,
 * so a denied or inactivated path hides the tab rather than failing it.
 */
import { catalogue } from './catalogue-path';
import { Maintenance, VaultManagement } from './declarations/index';
import { PATH_SEPARATOR, pathKey } from './path-rules';
import type { CataloguePath, SecurityPermission } from './types';

export interface RecordAccessRule {
  /** Opening the record in its tool: its Documents and Attributes tabs, and its generated documents. */
  readonly open: readonly SecurityPermission[];
  /** The Notes tab (view and add, as legacy's single `Notes` command). Absent: no notes for this record type. */
  readonly notes?: readonly SecurityPermission[];
  /** Editing attributes (`{tool}\Attributes {Entity}`). Absent: attributes are read-only. */
  readonly editAttributes?: SecurityPermission;
}

/**
 * A source table whose rows belong to different tools, told apart by one column of the row. The rule
 * is resolved per record (`recordAccessRuleFor`) from that column's value; a missing value resolves to
 * no rule, so access fails closed.
 */
interface RecordAccessByRecord {
  /** The `dbo` column whose value picks the variant (the server loads it from the record). */
  readonly discriminator: string;
  /** The variants for the rows whose discriminator equals `value` (compared case-insensitively). */
  readonly variants: ReadonlyArray<{ readonly value: string; readonly rule: RecordAccessRule }>;
  /** What `recordAccessRule()` (no record) returns for this table: the variant the SPA hosts that already exist assume. */
  readonly legacyDefault: RecordAccessRule;
  /** The variant for every row that matches no listed value. */
  readonly otherwise: RecordAccessRule;
}

const VM = VaultManagement;

/**
 * Every tool that books Ledger Transaction Groups and produces their documents: Cash Count, vault
 * transactions (and close/open), cashier shifts (approve, even exchange), My Cashier Management (read-only, so its users reach
 * Documents without the Cashier Management tool path) and adjusting entries.
 *
 * unit-39: the two Tip Drop child paths of Drop & Issue Station and Tip Employee Management are listed too (the narrowest tool-scoped
 * grant), so a user who holds only that Tip Drop path can open the Tip Form it just produced. Cashier Management and My Cashier
 * Management need nothing: their tool paths are already listed. This widens read access to every Ledger Transaction Group document by
 * id for holders of those two paths (SC-9, plan-manifest).
 */
const ledgerTransactionGroup: RecordAccessRule = {
  open: [
    VM.cashCountManagement.permission,
    VM.vaultTransactionManagement.permission,
    VM.cashierShiftManagement.permission,
    VM.cashierShiftManagementMyCashierManagementTool.permission,
    VM.createAdjustingEntries.permission,
    VM.dropAndIssueStationTipDrop.permission,
    VM.tipEmployeeManagementTipDrop.permission,
  ],
  notes: [VM.vaultTransactionManagementNotes.permission, VM.cashierShiftManagementNotes.permission],
};

const currencyBag: RecordAccessRule = {
  open: [VM.currencyBagManagement.permission],
};

/** `DepositCategoryType.DepositCategoryTypeId` of the Tip Pool type, owned by the Tip Pools tool. */
const TIP_POOL_TYPE_ID = '52EC6CD8-C0A3-4169-BF1C-0141383CEFD4';

/** Tip Pool rows of `DepositCategories`: opened and edited through Tip Pools only. */
const tipPoolCategory: RecordAccessRule = {
  open: [Maintenance.tipPools.permission],
  editAttributes: Maintenance.tipPoolsAttributesTipPool.permission,
};

/** Every other `DepositCategories` row: opened through Deposit Categories, attributes edited through its Attributes Tab. */
const depositCategory: RecordAccessRule = {
  open: [Maintenance.depositCategories.permission],
  editAttributes: Maintenance.depositCategoriesEditDepositCategoryAttributesTab.permission,
};

type RuleEntry = RecordAccessRule | RecordAccessByRecord;

const isByRecord = (entry: RuleEntry): entry is RecordAccessByRecord => 'discriminator' in entry;

/** Keyed by `permissionKey`-style lower case: SQL Server compares `SourceTableName` case-insensitively. */
const RULES = new Map<string, RuleEntry>([
  ['ledger transaction group', ledgerTransactionGroup],
  ['vault', { open: [VM.vaultTransactionManagement.permission] }],
  // Both spellings exist in customer data (`sd_SourceTable_CurrencyBag_LegacySpelling.sql`).
  ['currency bag', currencyBag],
  ['currencybag', currencyBag],
  ['tablecashcount', {
    open: [VM.tableCountManagement.permission],
    notes: [VM.tableCountManagementNotes.permission],
  }],
  ['machinecashcount', {
    open: [VM.machineCountManagement.permission],
    notes: [VM.machineCountManagementNotes.permission],
  }],
  ['generalledgeraccountcode', {
    open: [Maintenance.glAccountCodes.permission],
    editAttributes: Maintenance.glAccountCodesAttributesGlAccountCode.permission,
  }],
  // Revenue Centers: its Attributes tab (the Audit Types assignments the audit reports filter on). Legacy's
  // `AttributesRevenueCenterMaintenanceCommand` edits them under its own path.
  ['revenuecenter', {
    open: [Maintenance.revenueCenters.permission],
    editAttributes: Maintenance.revenueCentersAttributesRevenueCenter.permission,
  }],
  // Vault Locations: its Attributes tab (`dbo.SourceTable` name `VaultType`, legacy `_sourceTableName`). Legacy's
  // `AttributesVaultLocationMaintenanceCommand` edits them under its own path.
  ['vaulttype', {
    open: [Maintenance.vaultLocations.permission],
    editAttributes: Maintenance.vaultLocationsAttributesVaultLocation.permission,
  }],
  // Two tools share this table: Tip Pools owns the rows of the TipPool type, Deposit Categories owns the
  // rest (legacy's Deposit Categories tool hides tip pools). One rule per table, where any one path of
  // a list is enough, would let either tool's users reach the other's records, so the rule resolves
  // per record from the row's `DepositCategoryTypeId` (plan D20).
  ['depositcategories', {
    discriminator: 'DepositCategoryTypeId',
    variants: [{ value: TIP_POOL_TYPE_ID, rule: tipPoolCategory }],
    legacyDefault: tipPoolCategory,
    otherwise: depositCategory,
  }],
]);

const lookupRule = (sourceTable: string): RuleEntry | undefined => RULES.get(pathKey(sourceTable.trim()));

/**
 * The rule for a `dbo.SourceTable` name, or `undefined` when no tool owns that record type. For a table
 * whose rows belong to several tools this is the Tip Pools variant, which the SPA's existing hosts assume;
 * server code must use `recordAccessRuleFor` with the record's discriminator instead.
 */
export function recordAccessRule(sourceTable: string): RecordAccessRule | undefined {
  const entry = lookupRule(sourceTable);
  if (entry === undefined) return undefined;
  return isByRecord(entry) ? entry.legacyDefault : entry;
}

/**
 * The column whose value picks the rule for a record of this table, or `undefined` when one rule covers
 * the whole table. A caller that gets a name must load that column from the record and pass it to
 * `recordAccessRuleFor`.
 */
export function recordAccessDiscriminator(sourceTable: string): string | undefined {
  const entry = lookupRule(sourceTable);
  return entry !== undefined && isByRecord(entry) ? entry.discriminator : undefined;
}

/**
 * The rule for one record. A single-rule table returns its rule whatever `discriminatorValue` is. A table
 * resolved per record returns the variant for the value, and `undefined` (access refused) when the value
 * is missing or blank, so a caller that could not load the record fails closed.
 */
export function recordAccessRuleFor(
  sourceTable: string,
  discriminatorValue?: string | null,
): RecordAccessRule | undefined {
  const entry = lookupRule(sourceTable);
  if (entry === undefined || !isByRecord(entry)) return entry;
  if (typeof discriminatorValue !== 'string' || discriminatorValue.trim() === '') return undefined;
  const wanted = discriminatorValue.trim().toLowerCase();
  return entry.variants.find((v) => v.value.toLowerCase() === wanted)?.rule ?? entry.otherwise;
}

/** The root of legacy's per-attribute-type paths. */
export const ATTRIBUTES_ROOT = 'Attributes';
/** The system segment legacy uses when a source table has no external system. */
export const DEFAULT_ATTRIBUTE_SYSTEM = 'Insight Cash';

/**
 * Legacy's path for one attribute type: `Attributes\{System}\{SourceTable}\{AttributeType.Name}`,
 * where System is the source table's `ExternalSystem.Name`, or `Insight Cash` when it has none.
 * Each part is data, so it is validated: one that is blank or holds a backslash would address a
 * different node and is refused, as is a path that breaks the shared shape rules.
 */
export function attributeTypePermission(
  system: string | null | undefined,
  sourceTable: string,
  attributeTypeName: string,
): CataloguePath {
  const systemSegment = system?.trim() ? system.trim() : DEFAULT_ATTRIBUTE_SYSTEM;
  for (const segment of [systemSegment, sourceTable, attributeTypeName]) {
    if (segment.includes(PATH_SEPARATOR)) {
      throw new Error(`Security registry: attribute path segment "${segment}" contains a backslash.`);
    }
  }
  return catalogue([ATTRIBUTES_ROOT, systemSegment, sourceTable, attributeTypeName].join(PATH_SEPARATOR));
}
